import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { StudioPage, Panel, SectionLabel, Metric, Badge, DataTable, Loading, cx } from "./StudioShared";
import ActivityTimeline from "@/components/ActivityTimeline";
import { Server, Cpu, Route, Grid3x3, Lightbulb, RefreshCw, CheckCircle2, Zap, Activity } from "lucide-react";

const money = (c) => (c || c === 0 ? `$${Number(c).toFixed(5)}` : "—");
const ms = (v) => (v ? `${(v / 1000).toFixed(1)}s` : "—");
const STATUS_TONE = { online: "green", dormant: "slate", offline: "red", disabled: "amber" };
const TABS = [["providers", "Health Center", Server], ["models", "Model Registry", Cpu],
  ["routing", "Routing Policies", Route], ["matrix", "Capability Matrix", Grid3x3], ["optimize", "One-Click Optimize", Lightbulb], ["activity", "Activity", Activity]];

export default function ProviderManager() {
  const [tab, setTab] = useState("providers");
  return (
    <StudioPage title="AI Resource Manager" testid="resource-manager-page"
      question="Providers are resources, models are capabilities — AIPOS routes every mission to the best one. Nothing depends on a single provider.">
      <Panel className="p-1.5 mb-6 flex flex-wrap gap-1" data-testid="rm-tabs">
        {TABS.map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`rm-tab-${k}`}
            className={cx("flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-sm transition-colors", tab === k ? "bg-gold text-navy" : "text-slate-400 hover:text-white hover:bg-white/5")}>
            <Icon size={14} /> {l}
          </button>
        ))}
      </Panel>
      {tab === "providers" && <Providers />}
      {tab === "models" && <Models />}
      {tab === "routing" && <Routing />}
      {tab === "matrix" && <Matrix />}
      {tab === "optimize" && <Optimize />}
      {tab === "activity" && (
        <ActivityTimeline title="Provider & Routing Activity" subtitle="Model changes, routing updates, fallbacks and provider health — from the centralized Audit System"
          mode="global" params={{ category: "Provider" }} showFilters limit={40} testid="rm-activity-timeline"
          emptyText="No provider/routing activity recorded yet." />
      )}
    </StudioPage>
  );
}

function Providers() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/providers-registry").then((r) => setData(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const healthCheck = async () => { setBusy(true); try { await api.post("/admin/providers-registry/health-check"); toast.success("Health check complete."); load(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); } };
  if (!data) return <Loading label="Probing providers…" />;
  return (
    <div className="space-y-5" data-testid="rm-providers">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-400">{data.active.length} active · {data.providers.length - data.active.length} dormant</div>
        <button onClick={healthCheck} disabled={busy} data-testid="rm-health-check" className="flex items-center gap-1.5 border border-white/10 text-slate-200 px-3 py-2 text-sm rounded-sm hover:border-gold/50 hover:text-white disabled:opacity-60"><RefreshCw size={14} className={busy ? "animate-spin" : ""} /> Run Health Check</button>
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {data.providers.map((p) => (
          <Panel key={p.key} className="p-5" data-testid={`rm-provider-${p.key}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2"><Server size={16} className="text-gold" /><span className="text-white font-medium">{p.name}</span>{p.is_default && <Badge tone="gold">default</Badge>}</div>
              <Badge tone={STATUS_TONE[p.status] || "slate"} testid={`rm-provider-status-${p.key}`}>{p.status}</Badge>
            </div>
            <div className="grid grid-cols-2 gap-y-2 text-sm">
              <span className="text-slate-500">API key</span><span className={p.api_key_present ? "text-emerald-400" : "text-slate-400"}>{p.api_key_present ? "present" : "not set"}</span>
              <span className="text-slate-500">Avg latency</span><span className="text-slate-300">{ms(p.avg_latency_ms)}</span>
              <span className="text-slate-500">Error rate</span><span className={cx(p.error_rate > 10 ? "text-red-400" : "text-slate-300")}>{p.error_rate}%</span>
              <span className="text-slate-500">Executions</span><span className="text-slate-300">{p.executions}</span>
              <span className="text-slate-500">Last health</span><span className="text-slate-300">{p.last_health || "—"}</span>
            </div>
            {p.last_error && <div className="mt-2 text-xs text-red-300/80 truncate" title={p.last_error}>Last error: {p.last_error}</div>}
            <div className="mt-3 flex flex-wrap gap-1">{p.models.map((m) => <span key={m} className="text-[10px] text-slate-400 border border-white/10 rounded-sm px-1.5 py-0.5">{m}</span>)}</div>
          </Panel>
        ))}
      </div>
    </div>
  );
}

function Models() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/models-registry").then((r) => setData(r.data)); }, []);
  if (!data) return <Loading />;
  const columns = [
    { key: "label", label: "Model", render: (r) => <span className="text-white flex items-center gap-2">{r.label}{r.active ? <Badge tone="green">active</Badge> : <Badge tone="slate">dormant</Badge>}</span> },
    { key: "provider", label: "Provider" },
    { key: "tier", label: "Tier", render: (r) => <Badge tone={r.tier === "premium" ? "gold" : r.tier === "standard" ? "slate" : "green"}>{r.tier}</Badge> },
    { key: "cost", label: "Cost", render: (r) => <Ranks value={r.cost} invert /> },
    { key: "speed", label: "Speed", render: (r) => <Ranks value={r.speed} /> },
    { key: "quality", label: "Quality", render: (r) => <Ranks value={r.quality} /> },
    { key: "avg_cost", label: "Avg Cost", render: (r) => (r.metrics ? money(r.metrics.avg_cost) : "—") },
    { key: "avg_runtime", label: "Avg Time", render: (r) => (r.metrics ? ms(r.metrics.avg_runtime_ms) : "—") },
    { key: "success", label: "Success", render: (r) => (r.metrics ? `${r.metrics.success_rate}%` : "—") },
    { key: "runs", label: "Runs", render: (r) => (r.metrics ? r.metrics.executions : 0) },
    { key: "caps", label: "Capabilities", render: (r) => <div className="flex flex-wrap gap-1">{r.capabilities.map((c) => <span key={c} className="text-[10px] text-slate-400 border border-white/10 rounded-sm px-1.5 py-0.5">{c}</span>)}</div> },
  ];
  return <DataTable testid="rm-models-table" columns={columns} rows={data.models} empty="No models." />;
}

const Ranks = ({ value, invert }) => (
  <div className="flex gap-0.5">{[1, 2, 3, 4, 5].map((i) => <span key={i} className={cx("w-1.5 h-3 rounded-sm", i <= value ? (invert ? "bg-orange-400" : "bg-gold") : "bg-white/10")} />)}</div>
);

function Routing() {
  const [cfg, setCfg] = useState(null);
  const [models, setModels] = useState([]);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  useEffect(() => { api.get("/admin/routing-config").then((r) => setCfg(r.data)); api.get("/admin/models-registry").then((r) => setModels(r.data.models)); }, []);
  const policies = ["balanced", "cheapest", "fastest", "highest_quality", "manual"];
  const save = async () => { setBusy(true); try { await api.put("/admin/routing-config", { default_policy: cfg.default_policy }); toast.success("Routing policy saved."); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); } };
  const doPreview = async (policy) => { const r = await api.post("/admin/routing/resolve", { policy }); setPreview({ policy, ...r.data }); };
  if (!cfg) return <Loading />;
  return (
    <div className="space-y-5 max-w-2xl" data-testid="rm-routing">
      <Panel className="p-5">
        <SectionLabel className="mb-3">Default Routing Policy</SectionLabel>
        <p className="text-slate-500 text-sm mb-4">Applied to every AI node unless a workflow, mission, department or node overrides it.</p>
        <div className="flex flex-wrap gap-2">
          {policies.map((p) => (
            <button key={p} onClick={() => { setCfg({ ...cfg, default_policy: p }); doPreview(p); }} data-testid={`rm-policy-${p}`}
              className={cx("px-4 py-2 text-sm rounded-sm border capitalize transition-colors", cfg.default_policy === p ? "bg-gold text-navy border-gold" : "border-white/10 text-slate-300 hover:border-gold/50")}>{p.replace("_", " ")}</button>
          ))}
        </div>
        {preview && (
          <div className="mt-4 text-sm text-slate-400 border border-white/10 rounded-sm p-3" data-testid="rm-routing-preview">
            <span className="capitalize text-slate-500">{preview.policy.replace("_", " ")}</span> resolves to <span className="text-gold">{preview.model}</span> ({preview.provider}) — <span className="text-slate-500">{preview.reason}</span>
          </div>
        )}
        <button onClick={save} disabled={busy} data-testid="rm-routing-save" className="mt-4 bg-gold text-navy px-5 py-2 text-sm font-semibold rounded-sm hover:bg-gold-light disabled:opacity-60">Save policy</button>
      </Panel>
      <Panel className="p-5">
        <SectionLabel className="mb-2">Routing Overrides (precedence)</SectionLabel>
        <ol className="text-sm text-slate-400 space-y-1 list-decimal list-inside">
          <li>Node manual model / capability</li><li>Mission default</li><li>Department default</li><li>Workflow policy</li><li>Global default policy (above)</li>
        </ol>
        <p className="text-xs text-slate-500 mt-3">If a chosen model's provider is dormant, execution automatically falls back to the best active model (provider independence) and records it in Mission Replay.</p>
      </Panel>
    </div>
  );
}

function Matrix() {
  const [data, setData] = useState(null);
  const [models, setModels] = useState([]);
  const [edit, setEdit] = useState({});
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/capability-matrix").then((r) => { setData(r.data); setEdit(Object.fromEntries(r.data.matrix.map((m) => [m.capability, m.model]))); }), []);
  useEffect(() => { load(); api.get("/admin/models-registry").then((r) => setModels(r.data.models)); }, [load]);
  const save = async () => { setBusy(true); try { await api.put("/admin/routing-config", { capability_matrix: edit }); toast.success("Capability matrix saved."); load(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); } };
  if (!data) return <Loading />;
  return (
    <div className="space-y-4 max-w-3xl" data-testid="rm-matrix">
      <p className="text-slate-500 text-sm">Map each capability to the best model. AIPOS applies these automatically when a node declares a capability.</p>
      <Panel className="divide-y divide-white/5">
        {data.matrix.map((row) => (
          <div key={row.capability} className="flex items-center gap-4 px-4 py-3" data-testid={`rm-matrix-row-${row.capability}`}>
            <div className="flex-1 min-w-0"><div className="text-white text-sm">{row.capability}</div></div>
            <select value={edit[row.capability] || ""} onChange={(e) => setEdit({ ...edit, [row.capability]: e.target.value })} data-testid={`rm-matrix-select-${row.capability}`}
              className="bg-navy border border-white/10 px-3 py-1.5 text-sm text-white rounded-sm focus:border-gold focus:outline-none min-w-[200px]">
              {models.map((m) => <option key={m.id} value={m.id}>{m.label} ({m.provider}){m.active ? "" : " · dormant"}</option>)}
            </select>
          </div>
        ))}
      </Panel>
      <button onClick={save} disabled={busy} data-testid="rm-matrix-save" className="bg-gold text-navy px-5 py-2 text-sm font-semibold rounded-sm hover:bg-gold-light disabled:opacity-60">Save matrix</button>
    </div>
  );
}

function Optimize() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => api.get("/admin/optimize/opportunities").then((r) => setData(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const apply = async (o) => {
    setBusy(o.node_id + o.workflow_id);
    try { await api.post("/admin/optimize/apply", { kind: "workflow_node", workflow_id: o.workflow_id, node_id: o.node_id, model: o.suggested_model });
      toast.success(`Routed "${o.label}" → ${o.suggested_label}.`); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  };
  if (!data) return <Loading />;
  return (
    <div className="space-y-3" data-testid="rm-optimize">
      <div className="text-sm text-slate-400">{data.count} optimization opportunit{data.count === 1 ? "y" : "ies"} detected from real telemetry.</div>
      {data.opportunities.length === 0 ? <div className="text-slate-500 text-sm py-8 text-center">Everything is optimally routed. 🎯</div> : data.opportunities.map((o, i) => (
        <Panel key={i} className="p-4 flex items-center gap-4" data-testid={`rm-opp-${i}`}>
          <div className="w-9 h-9 rounded-sm bg-gold/10 border border-gold/40 flex items-center justify-center shrink-0"><Zap size={16} className="text-gold" /></div>
          <div className="min-w-0 flex-1">
            <div className="text-white text-sm truncate">{o.workflow_name} · <span className="text-slate-400">{o.label}</span></div>
            <div className="text-xs text-slate-500">{o.current_model} → <span className="text-emerald-400">{o.suggested_label}</span></div>
          </div>
          <div className="text-emerald-400 font-mono text-sm shrink-0">~{o.estimated_savings_pct}%</div>
          <button onClick={() => apply(o)} disabled={busy === o.node_id + o.workflow_id} data-testid={`rm-apply-${i}`}
            className="flex items-center gap-1.5 bg-gold text-navy px-3 py-1.5 text-xs font-semibold rounded-sm hover:bg-gold-light disabled:opacity-60 shrink-0"><CheckCircle2 size={13} /> Apply</button>
        </Panel>
      ))}
    </div>
  );
}
